import React, { FC, ReactElement, useState } from 'react';
import classNames from 'classnames';
import { gql, useApolloClient } from '@apollo/client';
import { Mention, MentionsInput, SuggestionDataItem } from 'react-mentions';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import Button, { BUTTON_TYPES, BUTTON_SIZES } from 'buyside/components/presentation/Button/button';
import ProfileImage, {
  IMAGE_SIZES
} from 'buyside/components/presentation/ProfileImage/profileImage';
import useMentionableAsset from 'buyside/components/container/Mentionable/useMentionableAsset';

import {
  useWhitePaperCommentMentionablesQuery,
  useCreateWhitePaperCommentMutation,
  WhitePaper,
  WhitePaperCommentsDocument,
  WhitePaperCommentsQuery
} from 'interfaces/graphql-typings';

import styles from './styles.scss';
import mentionStyling from 'buyside/styles/module/form/mentionStyling';

export const CurrentUser = gql`
  fragment CurrentUserWhitePaperCommentForm on User {
    profileImageUrl
  }
`;

const WhitePaperCommentForm: FC<{
  canCommentAnonymously: boolean;
  closeForm?: () => void;
  closeFormOpenThread?: () => void;
  currentUserId: string;
  isAuthor: boolean;
  isReplying?: boolean;
  parentId?: string;
  whitePaperId: string;
}> = ({
  canCommentAnonymously,
  currentUserId,
  closeForm,
  closeFormOpenThread,
  isAuthor,
  isReplying,
  parentId,
  whitePaperId
}) => {
  const client = useApolloClient();
  const [mentionableAssets, markup, renderSuggestion, displayTransform] = useMentionableAsset();

  const [isFocused, setIsFocused] = useState(Boolean(isReplying));

  const currentUser = client.readFragment({
    id: `User:${currentUserId}`,
    fragment: CurrentUser
  });

  const { profileImageUrl } = currentUser || {};

  const { data } = useWhitePaperCommentMentionablesQuery({ variables: { id: whitePaperId } });

  const mentionables = data?.whitePaper?.commentsMentionableList || [];

  const [createComment, { loading: mutationLoading, error }] = useCreateWhitePaperCommentMutation({
    update: (cache, { data }) => {
      const newComment = data?.createComment?.comment;
      if (!newComment) return;

      const cachedQuery = cache.readQuery<WhitePaperCommentsQuery>({
        query: WhitePaperCommentsDocument,
        variables: { id: whitePaperId }
      });
      if (!cachedQuery?.whitePaper?.comments) return;

      cache.writeQuery<WhitePaperCommentsQuery>({
        query: WhitePaperCommentsDocument,
        data: {
          ...cachedQuery,
          whitePaper: {
            ...cachedQuery.whitePaper,
            comments: {
              edges: [{ node: newComment }, ...(cachedQuery.whitePaper.comments.edges || [])]
            }
          }
        },
        variables: { id: whitePaperId }
      });

      cache.modify<WhitePaper>({
        id: cache.identify({ id: whitePaperId, __typename: 'WhitePaper' }),
        fields: { commentsCount: cachedCount => (cachedCount || 0) + 1 }
      });
    }
  });

  const defaultValues = {
    anonymous: false,
    body: '',
    requestResponse: false
  };

  const {
    control,
    formState: { errors, isSubmitting, isDirty },
    handleSubmit,
    register,
    reset
  } = useForm<typeof defaultValues>({
    defaultValues
  });

  const isLoading = mutationLoading || isSubmitting;

  const onSubmit: SubmitHandler<typeof defaultValues> = ({ anonymous, body, requestResponse }) =>
    createComment({
      variables: {
        anonymous,
        body,
        hideFromFundAdmins: false,
        parentId,
        requestResponse,
        whitePaperId
      }
    }).then((): void => {
      if (isReplying && closeFormOpenThread) {
        return closeFormOpenThread();
      }
      reset();
      setIsFocused(false);
    });

  return (
    <form
      className={classNames(styles.form, { [styles.isReplying]: isReplying })}
      onFocus={(): void => setIsFocused(true)}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className={styles.formLeft}>
        <ProfileImage source={profileImageUrl} size={IMAGE_SIZES.SMALL} />
      </div>
      <div className={styles.formRight}>
        <Controller
          control={control}
          name="body"
          rules={{ required: { message: 'Please enter a comment', value: true } }}
          render={({ field }): ReactElement => (
            <MentionsInput
              {...field}
              allowSpaceInQuery
              data-heap="comment-text-field"
              disabled={isLoading}
              id="showPageCommentTextArea"
              placeholder="Post a comment"
              style={mentionStyling}
            >
              <Mention
                appendSpaceOnAdd
                data={mentionables.filter(Boolean) as SuggestionDataItem[]}
                style={{ backgroundColor: '#DEEDF5' }}
                trigger="@"
              />
              <Mention
                appendSpaceOnAdd
                data={mentionableAssets}
                displayTransform={displayTransform}
                markup={markup}
                renderSuggestion={renderSuggestion}
                style={{ backgroundColor: 'rgb(255, 201, 201)' }}
                trigger="$"
              />
            </MentionsInput>
          )}
        />
        {isFocused && (
          <>
            {errors.body && <div className={styles.error}>{errors.body.message}</div>}
            <div className={styles.fieldNote}>
              <div>
                <strong>Type '@'</strong> to tag participants in this comment thread, users you
                follow, or users that are following you. Tagged members will be notified.
              </div>
              <div>
                <strong>Type '$'</strong> and begin typing to tag any ticker on SumZero. Members
                have watchlisted the tagged ticker will be notified.
              </div>
            </div>
            {!isAuthor && (
              <div>
                <label className={styles.checkbox}>
                  <input {...register('requestResponse')} disabled={isLoading} type="checkbox" />
                  Request a response from the author
                </label>
                {canCommentAnonymously && (
                  <label className={styles.checkbox}>
                    <input {...register('anonymous')} disabled={isLoading} type="checkbox" />
                    Post comment anonymously
                  </label>
                )}
              </div>
            )}
            {error && <div className={styles.error}>{error.graphQLErrors[0].message}</div>}
          </>
        )}
        <div className={styles.buttonArea}>
          <Button
            disabled={!isDirty}
            analyticsTag="post-comment"
            isLoading={isLoading}
            size={BUTTON_SIZES.SMALL}
            text={isReplying ? 'Post Reply' : 'Post Comment'}
            type={BUTTON_TYPES.PRIMARY}
          />
          {isReplying && (
            <Button
              disabled={isLoading}
              onClick={closeForm}
              size={BUTTON_SIZES.SMALL}
              text="Cancel"
              type={BUTTON_TYPES.SECONDARY}
            />
          )}
        </div>
      </div>
    </form>
  );
};

export default WhitePaperCommentForm;
